<template>
    <dialog-base
        :maxWidth="520"
        :showDialog="showDialog"
        @close="hide"
    >
        <dialog-header
            :title="t_title"
            @close="hide"
        />
        <dialog-body>
            <div class="pa-4 pt-2 overflow-hidden">
                <div class="edit-media-notifications">
                    <div v-if="multiedit" class="alert em-info"> {{t_multiedit_info}}</div>
                    <div v-if="Object.keys(errors).length > 0" class="alert alert-danger">
                        {{ t_error }}
                        <div class="pl-2" v-for="error in errors">
                            {{error}}
                        </div>
                    </div>
                </div>

                <div class="row m-b mt-1">
                    <div class="col-md-12 edit-media-modal-display-name-WORKAROUND">
                        <div class="row m-b">
                            <div class="col-md-12">
                                <text-field v-if="!multiedit"
                                    :label="t_media_title"
                                    :value="form.display_name"
                                    :rules="[rules.required]"
                                    @input="handleDisplayName"
                                />
                                <div v-else>
                                    <div>{{ t_media_titles }}:</div>
                                    <div class="clamped-height-container">
                                        <div class="entrynames-container text-center">
                                            {{sortedMediaNames.join(', ')}}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="row m-b">
                            <div class="col-md-12">
<!--                                    NOTE: If something gets changed here,
                                        don't forget to change it in add-media-modal.vue as well-->
                                    <autocomplete-addable
                                        :aria-label="t_tags"
                                        ref="tags"
                                        id="edit-tags"
                                        v-model="currentTags"
                                        :label="t_tags"
                                        :items="allTags.data"
                                        :popup-used-in-modal="true"
                                        :outlined="true" :dense="true" hide-details="auto"
                                        :multiple="true"
                                        clearable
                                        :creatable-values="tagCreationPrivilege === '1'">
                                    </autocomplete-addable>
                            </div>
                        </div>
                        <div class="row m-b">
                            <div class="col-md-12">
<!--                                    <chosen-multiselect id="edit-usergroups"-->
<!--                                                        v-model="currentUsergroups"-->
<!--                                                        :hierarchical="true">-->
<!--                                    </chosen-multiselect>-->
                                    <!-- TODO: add hierarchy -->
                                    <div class="d-flex">
                                        <v-autocomplete-custom
                                            :aria-label="t_usergroups"
                                            id="edit-usergroups"
                                            v-model="currentUsergroups"
                                            :label="t_usergroups"
                                            :items="allUsergroups.data"
                                            :popup-used-in-modal="true"
                                            multiple
                                            outlined
                                            dense
                                            clearable
                                            hide-details="auto">
                                            <template v-slot:append-outer>
                                                <v-tooltip-wrapper
                                                    v-if="t_usergroups_description"
                                                    :max-width="380"
                                                    content-class="tooltip-glass tooltip-white elevation-8">
                                                    <template #activator="{ on, attrs }">
                                                        <v-icon style="cursor: pointer"
                                                                v-bind="attrs"
                                                                v-on="on">
                                                            fa-light fa-circle-question
                                                        </v-icon>
                                                    </template>
                                                    {{ t_usergroups_description }}
                                                </v-tooltip-wrapper>
                                            </template>
                                        </v-autocomplete-custom>
                                    </div>
                                    <div class="mt-2" id="strict-checkbox">
                                        <Checkbox id="strict"
                                                  class="mr-2"
                                                  binary
                                                  :modelValue="strict_mode"
                                                  :label="t_strict_mode"
                                                  @click="strict_mode = !strict_mode"
                                        />
                                        {{ t_strict_mode }}
                                        <v-tooltip-wrapper activator="#strict-checkbox">
                                            <span>{{ t_strict_mode_description }}</span>
                                        </v-tooltip-wrapper>
                                    </div>
                            </div>
                        </div>
                        <div class="row m-b">
                            <div class="col-md-12">
                                <div v-bind:class="[(typeof errors.layers !== 'undefined' ? 'has-error':''),'form-group no-margins']">
                                    <v-autocomplete-custom
                                        :aria-label="t_layers"
                                        id="edit-layers"
                                        v-model='currentLayers'
                                        :label="t_layers"
                                        :items="allLayers.data"
                                        :popup-used-in-modal="true"
                                        multiple
                                        outlined
                                        dense
                                        clearable
                                        hide-details="auto">
                                    </v-autocomplete-custom>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6" :key="'datepicker-from' + form.id">
                                <datepicker
                                    v-model="valid_from"
                                    :label="t_label_start"
                                    :max="valid_to"
                                    @valid="valid => handleValidity('valid_from', valid)"
                                    popup-used-in-modal
                                />
                            </div>
                            <div class="col-md-6" :key="'datepicker-to' + form.id">
                                <datepicker
                                    v-model="valid_to"
                                    :label="t_label_end"
                                    :min="valid_from"
                                    @valid="valid => handleValidity('valid_to', valid)"
                                    align-right
                                    popup-used-in-modal
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </dialog-body>
        <div class="d-flex flex-row align-center justify-end pa-4">
            <media-buttons
                :disableSave="getValidity()"
                :saving="saving"
                :approvePrivilege="approvePrivilege === '1'"
                :publishPrivilege="publishPrivilege === '1'"
                :updateApprove="updateApprove"
                :updatePublish="updatePublish"
                @cycleUpdatePublish="cycleUpdatePublish"
                @cycleUpdateApprove="cycleUpdateApprove"
                @confirm="updateMediaWrapper"
            />
            <v-btn class="btn btn-secondary ml-1"  @click="hide">
                {{ t_cancel }}
            </v-btn>
        </div>
    </dialog-base>
</template>

<script>
    import modalMixin from "../../common/modal-mixin";
    import { formatDate, caseInsensitiveSort } from '../../../utility'
    import Datepicker from '../../common/datepicker.vue'
    import toaster from "../../common/toaster";
    import MediaButtons from "./components/publish-buttons.vue";
    import TextField from "../../common/text-field.vue";
    import Tags from "./add/tags.vue";
    import Usergroups from "./add/usergroups.vue";
    import Layers from "./add/layers.vue";
    import Template from "../elements/template.vue";
    import VTooltipWrapper from "../../common/v-tooltip-wrapper.vue";
    import Checkbox from "../common/PrimeVue/Checkbox.vue";
    import DialogBase from "../../dialog/components/dialog-base.vue";
    import DialogHeader from "../../dialog/components/dialog-header.vue";
    import DialogBody from "../../dialog/components/dialog-body.vue";

    export default {
        props: [
            'approvePrivilege',
            'publishPrivilege',
            'tagCreationPrivilege'
        ],
        mixins: [
            modalMixin,
        ],
        components: {
            DialogBody, DialogHeader, DialogBase,
            Checkbox,
            VTooltipWrapper,
            Template,
            Layers,
            Usergroups,
            Tags,
            TextField,
            MediaButtons,
            Datepicker
        },
        data: function () {
            return {
                t_title: Lang.langGet("Edit Media"),
                t_error: Lang.langGet("Failed to edit file:"),
                t_multiedit_info: Lang.langGet("You are editing multiple files. Tags, Usergroups and Layers will be set to the selected values for ALL elements."),
                t_media_title: Lang.langGet("Title"),
                t_media_titles: Lang.langGet("Titles"),
                t_tags: Lang.langChoice("Tag|Tags", 2),
                t_strict_mode: Lang.langGet("Strict Mode"),
                t_strict_mode_description: Lang.langGet("In \"Strict Mode\" users must have ALL selected usergroups."),
                t_usergroups: Lang.langChoice("Usergroup|Usergroups", 2),
                t_usergroups_description: Lang.langGet("If you leave this empty, the element will be available for everyone."),
                t_layers: Lang.langChoice("Layer|Layers", 2),
                t_label_start: Lang.langGet("Not before"),
                t_label_end: Lang.langGet("Not after"),
                t_cancel: Lang.langGet("Cancel"),
                t_usergroups_load_failed: Lang.langGet("Failed to load usergroups from database."),
                t_layers_load_failed: Lang.langGet("Failed to load layers from database."),
                t_tags_load_failed: Lang.langGet("Failed to load tags from database."),
                t_update_failed: Lang.langGet("Failed to modify the media file."),
                t_publish: Lang.langGet("Publish"),

                showDialog: false,

                invalidPropertyMap: {},
                multiedit: false,
                errors: {},
                form: {
                    id: null,
                    display_name: '',
                    valid_from: '',
                    valid_to: ''
                },
                currentTags: [],
                allTags: {
                    data: []
                },
                currentUsergroups: [],
                allUsergroups: {
                    data: []
                },
                currentLayers: [],
                allLayers: {
                    data: []
                },
                strict_mode: false,
                saving: false,
                modalName: 'edit-media',
                updatePublish: 0,
                updateApprove: -1,

                mediaNames: [],
            }
        },
        computed: {
            rules() {
                return {
                    required: value => !!value || Lang.langGet('You must enter a ') + Lang.langGet('name')
                }
            },
            valid_from: {
                get: function () {
                    return this.form.valid_from;
                },
                set: function (val) {
                    this.form.valid_from = val ?? '';
                }
            },
            valid_to: {
                get: function () {
                    return this.form.valid_to;
                },
                set: function (val) {
                    this.form.valid_to = val ?? '';
                }
            },
            sortedMediaNames() {
                return this.mediaNames.sort(caseInsensitiveSort)
            }
        },
        methods: {
            show() {
                this.showDialog = true;
            },
            hide() {
                this.showDialog = false;
            },
            handleDisplayName(value) {
                this.form.display_name = value;
                this.handleValidity('display_name', !!value);
            },
            handleValidity(property, validity) {
                const newMap = {...this.invalidPropertyMap};
                newMap[property] = validity;
                this.invalidPropertyMap = newMap;
            },
            getValidity() {
                const validity = _.values(this.invalidPropertyMap);
                return validity.some(flag => !flag);
            },
            reset: function () {
                _.extend(this.$data, this.$options.data());
                if (this.publishPrivilege && this.approvePrivilege) {
                    this.updatePublish = parseInt(localStorage.getItem("mediapool-publish-" + Laravel.activeProject) ?? 1);
                }
                if (this.approvePrivilege) {
                    this.updateApprove = parseInt(localStorage.getItem("mediapool-approve-" + Laravel.activeProject) ?? 1);
                }
                this.invalidPropertyMap = {};
            },
            loadUsergroups: function (medias) {
                if (this.allUsergroups.data.length > 0) {
                    return;
                }

                axios.post('/content/mediapool/loadusergroups', {
                    id: Laravel.activeProject
                }).then((response) => {
                    this.allUsergroups.data = response.data.data;
                    this.currentUsergroups = null;
                    medias.forEach(media => {
                        const usergroups = media.usergroups.map(usergroup => usergroup.id);
                        if (this.currentUsergroups === null) {
                            this.currentUsergroups = usergroups;
                        } else {
                            this.currentUsergroups = _.intersection(this.currentUsergroups, usergroups);
                        }
                    });
                    if (this.currentUsergroups === null) {
                        this.currentUsergroups = [];
                    }
                }).catch((error) => {
                    if (error.response?.status === 401) {
                        window.location = '/login';
                    }

                    toaster.error(this.t_usergroups_load_failed);
                });
            },
            loadLayers: function (medias) {
                if (this.allLayers.data.length > 0) {
                    return;
                }

                axios.post('/content/mediapool/loadlayers', {
                    id: Laravel.activeProject
                }).then((response) => {
                    this.allLayers.data = response.data.data;
                    this.currentLayers = null;
                    medias.forEach(media => {
                        const layers = media.layers.map(layer => layer.id);
                        if (this.currentLayers === null) {
                            this.currentLayers = layers;
                        } else {
                            this.currentLayers = _.intersection(this.currentLayers, layers);
                        }
                    });
                    if (this.currentLayers === null) {
                        this.currentLayers = [];
                    }
                }).catch((error) => {
                    if (error.response?.status === 401) {
                        window.location = '/login';
                    }

                    toaster.error(this.t_layers_load_failed);
                });
            },
            loadTags: function (medias) {
                if (this.allTags.data.length > 0) {
                    return;
                }

                axios.post('/content/mediapool/loadtags', {
                    id: Laravel.activeProject
                }).then((response) => {
                    let assignableSuperTags = response.data.data.superTags.filter((tag) => {
                        return tag.text === 'hidden';
                    });
                    _.each(assignableSuperTags, (tag, key) => {
                        assignableSuperTags[key].text = "<i class=\"far fa-sparkles\"></i> " + tag.text;
                    })
                    this.allTags.data = response.data.data.ordinaryTags.concat(assignableSuperTags);

                    this.currentTags = null;
                    medias.forEach(media => {
                        const tags = media.tags.map(tag => tag.id);
                        if (this.currentTags === null) {
                            this.currentTags = tags;
                        } else {
                            this.currentTags = _.intersection(this.currentTags, tags);
                        }
                    });
                    if (this.currentTags === null) {
                        this.currentTags = [];
                    }
                }).catch((error) => {
                    if (error.response?.status === 401) {
                        window.location = '/login';
                    }

                    toaster.error(this.t_tags_load_failed);
                });
            },
            updateMediaWrapper: function (approve=0, publish=0) {
                if (this.saving === true) {
                    return;
                }

                this.errors = {};
                this.saving = true;
                let data = {
                    approve: approve,
                    publish: publish
                };

                if (data.publish && !localStorage.getItem("mediapool-autopublish-confirm-" + Laravel.activeProject)) {
                    EaseEventBus.fire('showAutopublishWarning', {data: data, type: 'mediapool', task: 'Save', id: 'edit-media'});
                } else {
                    this.updateMedia(data);
                }
            },
            updateMedia: function (data) {
                axios.post('/content/mediapool/edit', {
                    'id': this.form.id,
                    'valid-from': this.form.valid_from,
                    'valid-to': this.form.valid_to,
                    'display_name': this.form.display_name,
                    'usergroups': this.currentUsergroups,
                    'layers': this.currentLayers,
                    'tags': this.currentTags,
                    'strict_mode': this.strict_mode,
                    'approve': data.approve,
                    'publish': data.publish
                }).then(() => {
                    this.saving = false;
                    EaseEventBus.fire('updateMedialist');
                    EaseEventBus.fire('hideModal', this.modalName);
                }).catch((error) => {
                    this.saving = false;

                    if (error.response?.status === 401) {
                        window.location = '/login';
                    } else if (error.response?.status === 422) {
                        let tmpErrors = {};

                        for (let key in error.response?.data?.errors) {
                            tmpErrors[key] = error.response.data.errors[key][0];
                        }

                        this.errors = _.extend({}, this.errors, tmpErrors);
                    } else {
                        toaster.error(this.t_update_failed);
                    }
                });
            },
            cycleUpdatePublish: function () {
                this.updatePublish = (parseInt(this.updatePublish) + 1) % 2 || 0;
                localStorage.setItem("mediapool-publish-" + Laravel.activeProject, this.updatePublish);
            },
            cycleUpdateApprove: function () {
                this.updateApprove = (parseInt(this.updateApprove) + 1) % 3 || 0;
                this.updateApprove = this.updateApprove === 2 ? -1 : this.updateApprove;
                localStorage.setItem("mediapool-approve-" + Laravel.activeProject, this.updateApprove);
            }
        },
        mounted() {
            EaseEventBus.listen('editMedia', (selectedMediaFiles) => {
                this.reset();

                if (Array.isArray(selectedMediaFiles) && selectedMediaFiles.id === undefined) {
                    this.multiedit = true;
                } else {
                    selectedMediaFiles = [selectedMediaFiles];
                    this.multiedit = false;
                }
                this.loadUsergroups(selectedMediaFiles);
                this.loadLayers(selectedMediaFiles);
                this.loadTags(selectedMediaFiles);
                this.form.id = selectedMediaFiles.map(mediaFile => mediaFile.id)
                this.mediaNames = selectedMediaFiles.map(mediaFile => mediaFile.name)
                this.form.display_name = this.mediaNames.join(', ')

                let value;
                value = _.uniq(selectedMediaFiles.map(media => media.parameters['valid-from'] ));
                this.form.valid_from = value.length === 1 ? value[0] : '';
                value = _.uniq(selectedMediaFiles.map(media => media.parameters['valid-to'] ));
                this.form.valid_to = value.length === 1 ? value[0] : '';
                value = _.uniq(selectedMediaFiles.map(media => media.strict_mode ));
                this.strict_mode = value.length === 1 ? value[0] : '';
                EaseEventBus.fire('showModal', this.modalName);

                this.show();
            });

            EaseEventBus.listen('hideModal', (modal) => {
                if (modal === this.modalName) {
                    this.reset();
                    this.hide();
                }
            });

            EaseEventBus.listen('updateProceed', (data) => {
                if (data.type === 'mediapool' && data.task === 'Save' && data.id === 'edit-media') {
                    if (data.proceed) {
                        this.updateMedia(data.data);
                    } else {
                        this.saving = false;
                    }
                }
            });
        }
    }
</script>

<style scoped>
    .em-info {
        color: var(--color-text-dark);
        background-color: var(--color-warning);
    }
</style>
